/**
 * BrandSplash — the boot / "still warming up" screen shown while the app
 * decides whether the user has a live session (App.tsx `!ready`).
 *
 * It animates the official CampusHubAI app icon (the same artwork as
 * favicon.ico and the PWA icons) rather than a placeholder "CH" monogram:
 *   • the mark floats and breathes behind a soft gold halo,
 *   • a gold arc ring sweeps around it as the progress spinner,
 *   • a light sheen glides across the mark,
 *   • the "CampusHub" wordmark rises in with a gradient,
 *   • the "Loading" label carries three staggered dots.
 *
 * All motion is pure CSS keyframes (see .brand-splash* in globals.css) so it
 * survives MotionConfig reducedMotion="always" on native and stays
 * compositor-only (transform/opacity). Honours prefers-reduced-motion.
 */
import { useEffect } from "react";
import { Capacitor } from "@capacitor/core";
import { SplashScreen } from "@capacitor/splash-screen";

export function BrandSplash() {
  // The Capacitor plugin splash (launchAutoHide:false) otherwise stays up —
  // covering this animated loader — until NativeLifecycle mounts, which only
  // happens after the session probe resolves (App.tsx `!ready`). Hide it as
  // soon as this paints so the user sees an animated "loading" screen (the
  // real logo + spinner) instead of a frozen static logo for the whole boot.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    const id = requestAnimationFrame(() => {
      void SplashScreen.hide({ fadeOutDuration: 200 }).catch(() => {});
    });
    return () => cancelAnimationFrame(id);
  }, []);

  return (
    <div className="ch-native-splash brand-splash">
      <div className="brand-splash__stage">
        <span aria-hidden className="brand-splash__halo" />
        <span aria-hidden className="brand-splash__ring" />
        <span className="brand-splash__mark">
          <CampusMark />
          <span aria-hidden className="brand-splash__sheen" />
        </span>
      </div>

      <div className="brand-splash__wordmark" aria-hidden>CampusHubAI</div>

      <p className="brand-splash__text" role="status" aria-live="polite">
        Loading
        <span aria-hidden className="brand-splash__dots">
          <i /><i /><i />
        </span>
      </p>
    </div>
  );
}

/**
 * The official app icon, rendered from the artwork file.
 *
 * This used to be a hand-drawn SVG of a gold campus building — the previous
 * CampusHub identity. It is deliberately not redrawn in code now: the mark
 * must be exactly the supplied artwork, and an inline copy is one more place
 * for the brand to drift.
 */
function CampusMark() {
  return (
    <img
      src="/icons/icon-192.png"
      width={60}
      height={60}
      alt="CampusHubAI"
      decoding="async"
      style={{ display: "block", width: 60, height: 60, objectFit: "contain" }}
    />
  );
}
